import { oldeWoodPhotoAlt, RECLAIMED_SHARE_IMAGE } from "@/data/reclaimedOldeImages";
import useReclaimedOldePhotos from "@/hooks/useReclaimedOldePhotos";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import UtilBar from "@/components/UtilBar";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import QuoteModal from "@/components/QuoteModal";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import heroImg from "@/assets/reclaimed-hero.webp";
const awardImg = "/images/site/reclaimed-barnwood-floor-rubio-monocoat-farmington-hills-mi.webp";
import reclaimedVentImg from "@/assets/reclaimed-project-vent.webp";
import ReclaimedProjectsGallery from "@/components/ReclaimedProjectsGallery";
import PageFAQ, { FAQItem } from "@/components/PageFAQ";
import usePageSEO, { makeArticleSchema } from "@/hooks/usePageSEO";
import { reclaimedProducts } from "@/data/oldeWoodReclaimedData";
import { useEffect } from "react";

// One-line character descriptions per Olde Wood Antique Reclaimed species.
// Keyed by the exact product name in oldeWoodReclaimedData.ts.
const speciesBlurbs: Record<string, string> = {
  "Antique Reclaimed Oak": "Dense tight grain and deep patina  the workhorse of American barnwood.",
  "Reclaimed Oak  Appearance Grade White Oak": "Cleaner face with subtle character  a calmer reclaimed look for modern interiors.",
  "Reclaimed Oak Hit-Skip": "Original band-saw kerf preserved across the face for maximum texture.",
  "Reclaimed Resawn Oak": "Freshly resawn face reveals tight old-growth grain under century-old timber.",
  "Reclaimed Resawn Oak  Appearance Grade White Oak": "Resawn white oak with subdued character, contemporary-leaning.",
  "Reclaimed Oak  Original Face": "Untouched weathered face with nail holes, checks, and full patina intact.",
  "Reclaimed Historic Plank": "Mixed hardwood blend salvaged from historic timber-frame structures.",
  "Reclaimed Harvest Plank": "Warm mixed-hardwood harvest tones milled to wide plank.",
  "Reclaimed Hickory": "High density and dramatic color variation  one of the toughest reclaimed species.",
  "Reclaimed Beech/Maple": "Lighter tones and tight grain with subtle antique character.",
  "Reclaimed Wormy Chestnut": "Rare American chestnut with wormhole character  effectively impossible to source new.",
  "Reclaimed White Pine": "Softer species with amber patina and generous knot character.",
  "Reclaimed Tobacco Pine": "Deep tobacco-brown patina from decades inside Southern tobacco barns.",
  "Reclaimed Heart Pine Hit-Skip": "Dense old-growth heart pine with preserved saw-kerf texture.",
  "Reclaimed Heart Pine  Select": "Cleanest heart pine grade with tight vertical grain.",
  "Reclaimed Heart Pine  Common": "Character-forward heart pine with knots and rich color variation.",
  "Reclaimed Heart Pine  Original Face": "Weathered heart pine face, untouched patina straight from the barn.",
  "Reclaimed Trophy Room": "Premium mixed reclaimed species blend curated for statement rooms.",
  "Reclaimed Oak Trophy Room": "Premium oak-only reclaimed selection, curated for maximum character.",
  "Reclaimed Douglas Fir": "Wide-grain Pacific Northwest fir with warm honey tones and old-growth density.",
};

const processCards = [
  {
    icon: "🪵",
    title: "Careful Extraction & Preservation.",
    desc: "Every timber is carefully dismantled, de-nailed, and kiln-dried to eliminate insects and ensure structural integrity. We treat each board as an artifact. Because that's exactly what it is.",
  },
  {
    icon: "⚙️",
    title: "Honoring the Original Carpenters.",
    desc: "Using specialized trade skills, we mill the wood while intentionally preserving the original saw marks and patina left by carpenters over a century ago. Their craftsmanship becomes part of your home's story.",
  },
  {
    icon: "🎨",
    title: "Installation by an Artist.",
    desc: "Because every single board is uniquely different, reclaimed wood can't be installed like production flooring. It demands an artist's eye  balancing character, color, and grain plank by plank.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] as const },
};

const reclaimedFAQs: FAQItem[] = [
  {
    q: "How much does reclaimed barnwood flooring cost in Michigan?",
    a: (
      <>
        Reclaimed wood flooring in Michigan typically starts in the mid-teens per square foot for material and rises from there depending on species, plank width, grade, and finish. Rare species such as Wormy Chestnut and wide-plank Heart Pine sit well above common Reclaimed Oak. Installed pricing also reflects subfloor preparation, acclimation time, and finish system  see our{" "}
        <Link to="/installation-cost" className="text-primary underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors">installation cost breakdown</Link> for how each variable moves the number. We quote reclaimed as a full turnkey scope, never a per-square-foot commodity price.
      </>
    ),
  },
  {
    q: "Is reclaimed wood structurally sound after 100+ years?",
    a: (
      <>
        Yes  and often more stable than newly kiln-dried lumber. Old-growth timber was harvested from slower-grown trees with tighter grain and higher density than most modern hardwood. As an NWFA Certified Wood Flooring Inspector, we grade every reclaimed plank for checks, splits, insect damage, and structural integrity before it enters the mill. Nail holes and surface character do not compromise structural performance when the plank has been properly graded and re-milled.
      </>
    ),
  },
  {
    q: "How long does reclaimed wood need to acclimate before installation?",
    a: "Reclaimed material carries a longer moisture history than kiln-dried new hardwood and needs additional acclimation time on-site before installation. We follow NWFA guidelines for equilibrium moisture content (EMC) targets specific to Southeast Michigan and monitor both the wood and the subfloor with pin meters until they are within spec. Acclimation windows for wide-plank reclaimed are typically longer than for a comparable new hardwood installation  we set expectations in the project timeline before crews arrive.",
  },
  {
    q: "Can reclaimed flooring be refinished later?",
    a: (
      <>
        In most cases, yes. Solid reclaimed and thick-sawn engineered reclaimed both retain a meaningful wear layer that can be sanded and refinished multiple times. Reclaimed with an intentional Original Face or Hit-Skip texture is a different conversation  aggressive sanding removes the character that made you buy the floor in the first place, so we usually recommend a screen-and-recoat rather than a full sand. See our{" "}
        <Link to="/refinishing-cost" className="text-primary underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors">refinishing cost guide</Link> for how we approach reclaimed refinishing scopes.
      </>
    ),
  },
  {
    q: "Does reclaimed wood work with radiant floor heat?",
    a: "It can, with the right species and platform. Denser reclaimed species and quarter-sawn cuts move less seasonally and are the safer choice over radiant. Wide-plank flat-sawn Oak or Heart Pine over radiant requires an engineered platform, careful moisture management, and radiant system commissioning per NWFA guidelines before installation. We evaluate radiant compatibility during the site visit rather than assuming the answer.",
  },
  {
    q: "What is the lead time to source reclaimed for a Michigan project?",
    a: "Common Reclaimed Oak grades can often move in a few weeks. Rare species (Wormy Chestnut, wide-plank Heart Pine, specific Trophy Room selections) and any custom mill run should be planned as a multi-month lead time. We source through our Olde Wood Limited partnership, confirm inventory in writing, and lock lead time into the project schedule before the deposit  no surprises at framing stage.",
  },
];

const ReclaimedBarnwood = () => {
  useReclaimedOldePhotos("/reclaimed-barnwood");
  usePageSEO({
    title: "Reclaimed Barnwood Flooring in Michigan | 1 Wood Floors",
    description: "Reclaimed barnwood flooring in Michigan: 20 antique species from barns over a century old, installed by a team holding Four NWFA Certifications.",
    path: "/reclaimed-barnwood",
    image: RECLAIMED_SHARE_IMAGE,
  });

  // Article JSON-LD for this category hub (FAQPage JSON-LD is injected by PageFAQ).
  useEffect(() => {
    const article = makeArticleSchema(
      "Reclaimed Barnwood Flooring in Michigan",
      "Reclaimed wood flooring in Michigan from Olde Wood Limited, installed by 1 Wood Floors  a team holding Four NWFA Certifications (Installer, Sand & Finisher, Inspector, Sales Advisor) and 2017 NWFA Wood Floor of the Year winner."
    );
    let el = document.getElementById("reclaimed-article-jsonld") as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement("script");
      el.id = "reclaimed-article-jsonld";
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(article);
    return () => { el?.remove(); };
  }, []);

  return (
    <div className="min-h-screen">
      <UtilBar />
      <SiteHeader />
      <main>

      {/* ══ 1 · HERO ══ */}
      <section className="relative h-[70svh] min-h-[480px] max-h-[720px] flex items-end overflow-hidden">
        <PageBreadcrumb variant="overlay" items={[
          { label: "Home", to: "/" },
          { label: "Services" },
          { label: "Reclaimed Barnwood" },
        ]} />
        <div className="absolute inset-0">
          <img width={1920} height={1080} loading="eager" decoding="async" fetchPriority="high"
            src={heroImg}
            alt="Rustic reclaimed barnwood wide plank floor"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/95 via-foreground/60 to-foreground/20" />

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 pb-10 md:pb-16 px-5 md:px-10 lg:px-16 max-w-4xl"
        >
          <p className="text-[10px] md:text-[11px] font-semibold tracking-[.14em] uppercase text-primary-foreground/80 mb-3 md:mb-4 flex items-center gap-2">
            <span className="w-7 h-px bg-primary inline-block" />
            Reclaimed &amp; Wide Plank Flooring · Michigan
          </p>
          <h1 className="font-display text-[clamp(28px,5vw,56px)] font-semibold leading-[1.08] tracking-tight text-primary-foreground">
            Reclaimed Barnwood Flooring in Michigan
          </h1>
          <p className="mt-4 md:mt-6 text-[13px] md:text-[15px] font-light text-primary-foreground/60 max-w-lg leading-relaxed">
            Reclaimed wood flooring Michigan homeowners have trusted for three decades. Milled from 100+ year-old American barns through our exclusive Olde Wood Ltd partnership and installed by a team holding Four NWFA Certifications  Installer, Sand &amp; Finisher, Inspector, and Sales Advisor under one roof.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-6 md:mt-8">
            <QuoteModal
              trigger={
                <button className="bg-primary text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:bg-[hsl(var(--gold-400))] transition-colors text-center">
                  Schedule a Call →
                </button>
              }
            />
            <a
              href="#gallery"
              className="border border-primary-foreground/25 text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:border-primary-foreground/60 transition-colors text-center"
            >
              View Our Gallery
            </a>
          </div>
        </motion.div>
      </section>

      {/* ══ 1b · CREDENTIAL / AUTHORITY BAND (NEW) ══ */}
      <section className="bg-background border-b border-border/60 py-12 md:py-16">
        <div className="max-w-[900px] mx-auto px-5 md:px-12">
          <motion.div {...fadeUp}>
            <p className="text-[10px] md:text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-4">
              Michigan's Reclaimed Authority
            </p>
            <p className="text-[15px] md:text-[16px] font-light text-foreground/85 leading-[1.85]">
              1 Wood Floors is Michigan's reclaimed wood flooring specialist holding Four NWFA Certifications  the same team holds Four NWFA Certifications as Installer, Sand &amp; Finisher, Wood Flooring Inspector, and Sales Advisor, alongside a Licensed Michigan Residential Builder credential and full insurance. We earned the 2017 NWFA Wood Floor of the Year (Best Restoration Makeover) on a reclaimed project  a West Virginia barn re-milled and installed in Farmington Hills. For three decades we have installed reclaimed material in Michigan homes, and we hold an exclusive Michigan partnership with Olde Wood Limited for their Antique Reclaimed collection. Reclaimed flooring is a technical discipline distinct from new hardwood installation; the credential stack above is the one you want on the job.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ══ 2 · 2017 NATIONAL AWARD WINNER ══ */}
      <section className="bg-foreground py-16 md:py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center">
            <motion.div {...fadeUp} className="relative">
              <div className="rounded-2xl overflow-hidden relative group">
                <img width={800} height={534} loading="lazy"
                  src={awardImg}
                  alt="Reclaimed barnwood floor with a Rubio Monocoat finish, Farmington Hills, MI"
                   className="w-full aspect-[4/3] object-cover transition-all duration-700 ease-out grayscale-[20%] contrast-[1.1] group-hover:grayscale-0 group-hover:scale-[1.04]"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground/80 via-foreground/40 to-transparent p-4 pt-12 md:p-5 md:pt-16">
                  <h3 className="font-display text-sm md:text-base font-semibold text-primary-foreground leading-tight">
                    Reclaimed Barnwood · Rubio Monocoat White 2C Oil
                  </h3>
                  <p className="text-[10px] md:text-xs tracking-[.12em] uppercase text-primary-foreground/70 mt-1">
                    Farmington Hills, Michigan · NWFA Wood Floor of the Year 2017
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }}>
              <p className="text-[10px] md:text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-4">
                🏆 2017 NWFA Wood Floor of the Year: Best Restoration Makeover
              </p>
              <h2 className="font-display text-[clamp(26px,4vw,44px)] font-semibold text-primary-foreground leading-[1.12]">
                From a West Virginia Barn to a Michigan{" "}
                <em className="italic font-normal text-primary">Masterpiece.</em>
              </h2>
              <p className="mt-5 text-sm md:text-[15px] font-light text-muted-foreground/80 leading-relaxed max-w-lg">
                This floor was crafted from a meticulous blend of reclaimed White Oak and Red Oak, salvaged directly from a historic barn in West Virginia. We custom-milled every plank and finished them with Rubio Monocoat, a plant-based oil that lets the wood's century-old character speak for itself. When you choose reclaimed, you aren't just buying a floor. You're reclaiming a piece of history.
              </p>
              <p className="mt-4 text-sm md:text-[15px] font-light text-muted-foreground/80 leading-relaxed max-w-lg">
                Explore our full <Link to="/brands/olde-wood/antique-reclaimed" className="text-primary hover:text-[hsl(var(--gold-300))] underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors">reclaimed wood flooring</Link> collection from Olde Wood Limited, with 20 antique species milled from authentic American barnwood.
              </p>
              <div className="flex flex-wrap gap-2 mt-6">
                {["Mixed Red & White Oak", "West Virginia Barn", "Rubio Monocoat", "Custom Milled", "LEED Qualified"].map(
                  (chip) => (
                    <span
                      key={chip}
                      className="text-[9px] font-bold tracking-[.1em] uppercase text-[hsl(var(--gold-200))] border border-primary-foreground/10 rounded-md px-3 py-1.5 bg-primary-foreground/[.04]"
                    >
                      {chip}
                    </span>
                  )
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ 2b · WHAT RECLAIMED BARNWOOD ACTUALLY IS (NEW) ══ */}
      <section className="bg-background py-14 md:py-20 lg:py-28 border-b border-border/40">
        <div className="max-w-[820px] mx-auto px-5 md:px-12">
          <motion.div {...fadeUp}>
            <p className="text-[11px] font-semibold tracking-[.1em] uppercase text-primary mb-2">Background</p>
            <h2 className="font-display text-[clamp(28px,5vw,48px)] font-semibold text-foreground leading-[1.1] mb-6">
              What reclaimed barnwood <em className="italic font-normal text-primary">actually</em> is.
            </h2>
            <div className="space-y-5 text-[15px] font-light text-muted-foreground leading-[1.85]">
              <p>
                Reclaimed barnwood flooring is milled from timber salvaged out of century-old American barns, factories, granaries, and timber-frame structures  primarily across the Northeast and Midwest US. The wood we source through Olde Wood Ltd is typically 100 to 200 years old at the time of milling, cut from old-growth forests that no longer exist in commercial supply.
              </p>
              <p>
                That single fact  old-growth versus new-growth  is what makes reclaimed structurally different from anything you can buy new. Old-growth timber was harvested from trees that grew slowly under a dense forest canopy for two centuries or more. The result is tighter grain, higher density, and greater dimensional stability once acclimated than the fast-grown plantation lumber that dominates today's hardwood supply.
              </p>
              <p>
                Reclaimed is graded differently as well. New hardwood is graded on absence of defects  Select, #1 Common, #2 Common. Reclaimed is graded character-forward: nail holes, weathering marks, saw kerf, hand-hewn texture, insect tracks, and color variation are features, not flaws. The grading conversation is about which character you want, at what density, and whether you prefer the original weathered face or a freshly resawn face that exposes the tight interior grain.
              </p>
              <p>
                Species availability tracks what was originally built with. Reclaimed Oak (Red and White), Heart Pine, Hickory, and mixed hardwood are the workhorses. Wormy Chestnut is rare  the American chestnut was effectively wiped out by blight a century ago, so every board is genuinely irreplaceable. Douglas Fir, White Pine, Tobacco Pine, and Beech/Maple round out the catalogue.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══ 2c · 20-SPECIES GRID (NEW) ══ */}
      <section className="bg-muted/30 py-14 md:py-20 lg:py-24 border-b border-border/40">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fadeUp} className="max-w-2xl mb-10">
            <p className="text-[11px] font-semibold tracking-[.1em] uppercase text-primary mb-2">Olde Wood Antique Reclaimed  20 Species</p>
            <h2 className="font-display text-[clamp(28px,5vw,48px)] font-semibold text-foreground leading-[1.1]">
              Reclaimed wood flooring, <em className="italic font-normal text-primary">by species.</em>
            </h2>
            <p className="mt-4 text-[14.5px] font-light text-muted-foreground leading-[1.8]">
              Every tile below links into the full Olde Wood Antique Reclaimed catalogue where you can review grade, plank width, and finish options in detail.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {reclaimedProducts.map((p) => (
              <Link
                key={p.name}
                to="/brands/olde-wood/antique-reclaimed"
                className="group rounded-xl overflow-hidden bg-background border border-border/50 hover:border-primary/50 transition-colors"
              >
                <div className="aspect-[16/10] overflow-hidden bg-muted">
                  <img
                    src={p.img}
                    alt={oldeWoodPhotoAlt(p.img, `Olde Wood ${p.name} reclaimed wood flooring sample`, true)}
                    width={480}
                    height={300}
                    loading="lazy"
                    className="w-full h-full object-cover grayscale-[15%] contrast-[1.05] group-hover:grayscale-0 group-hover:scale-[1.04] transition-all duration-500"
                  />
                </div>
                <div className="p-4">
                  <p className="text-[9px] font-bold tracking-[.14em] uppercase text-primary mb-1.5">
                    {p.species}
                  </p>
                  <h3 className="font-display text-[15px] font-semibold text-foreground leading-tight mb-2">
                    {p.name.replace(/  /g, "  ")}
                  </h3>
                  <p className="text-[12px] font-light text-muted-foreground leading-[1.6]">
                    {speciesBlurbs[p.name] ?? "Authentic Olde Wood antique reclaimed wide plank."}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          <motion.div {...fadeUp} className="mt-10 text-center">
            <Link
              to="/brands/olde-wood/antique-reclaimed"
              className="inline-flex items-center gap-2 text-[13px] font-semibold text-primary hover:text-[hsl(var(--gold-300))] transition-colors"
            >
              View the full Olde Wood Antique Reclaimed catalogue →
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ══ 3 · THE 30 HANDS ARTISAN PROCESS ══ */}
      <section className="bg-background py-14 md:py-20 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fadeUp} className="mb-8 md:mb-12 max-w-2xl">
            <p className="text-[11px] font-semibold tracking-[.1em] uppercase text-[hsl(var(--gold-400))] mb-2 flex items-center gap-2">
              The Artisan Process
              <span className="flex-1 h-px bg-border" />
            </p>
            <h2 className="font-display text-[clamp(28px,5vw,52px)] font-semibold text-foreground leading-[1.1]">
              A Journey of <em className="italic font-normal text-primary">30 Hands.</em>
            </h2>
            <p className="mt-4 text-sm md:text-[15px] font-light text-muted-foreground leading-relaxed">
              By the time a reclaimed floor, ceiling, or beam enters your home, it has passed through an average of 30 skilled hands. We don't just cut wood. We preserve the stories embedded in every grain.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {processCards.map((card, i) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                className="border border-border/50 rounded-2xl p-6 md:p-8 flex flex-col"
              >
                <div className="text-3xl mb-4">{card.icon}</div>
                <h4 className="font-display text-xl md:text-[22px] font-semibold text-foreground mb-3 leading-snug">
                  {card.title}
                </h4>
                <p className="text-[12px] md:text-[13px] font-light text-muted-foreground leading-relaxed flex-grow">
                  {card.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ 3a · WHY RECLAIMED INSTALLS DIFFERENTLY (NEW) ══ */}
      <section className="bg-background py-14 md:py-20 lg:py-28 border-t border-border/40">
        <div className="max-w-[820px] mx-auto px-5 md:px-12">
          <motion.div {...fadeUp} className="mb-8">
            <p className="text-[11px] font-semibold tracking-[.1em] uppercase text-primary mb-2">NWFA Inspector Perspective</p>
            <h2 className="font-display text-[clamp(28px,5vw,48px)] font-semibold text-foreground leading-[1.1]">
              Why reclaimed installs differently than <em className="italic font-normal text-primary">new hardwood.</em>
            </h2>
            <p className="mt-4 text-[14.5px] font-light text-muted-foreground leading-[1.85]">
              Reclaimed material behaves differently at every stage: moisture, structure, subfloor tolerance, and finish chemistry. What follows is the framework we apply on every reclaimed project as NWFA Certified Wood Flooring Inspectors.
            </p>
          </motion.div>

          <div className="space-y-8 text-[14.5px] font-light text-muted-foreground leading-[1.85]">
            <div>
              <h3 className="font-display text-[20px] md:text-[22px] font-semibold text-foreground mb-3">Moisture history and acclimation</h3>
              <p>
                Century-old wood carries a moisture story you cannot skip. Unlike new kiln-dried lumber that leaves the mill at a controlled moisture content, reclaimed timber has spent decades absorbing and releasing moisture through seasonal cycles. On arrival we measure moisture content with pin meters, compare it against the subfloor and the room's expected equilibrium moisture content (EMC) per NWFA guidelines, and set an acclimation window before installation begins. Skipping this step is the single most common cause of reclaimed floor failure.
              </p>
            </div>

            <div>
              <h3 className="font-display text-[20px] md:text-[22px] font-semibold text-foreground mb-3">Nail holes and structural integrity</h3>
              <p>
                Nail holes and surface checks are aesthetic features when the plank is structurally sound  and defects when it isn't. As certified inspectors we grade every plank before it enters the mill: sound wood around the hole, no through-checks that compromise the tongue or groove, no active insect damage, no rot below surface patina. Planks that fail grading come out of the run. This is why reclaimed sourced through a certified team looks and performs differently than salvage-yard barnwood.
              </p>
            </div>

            <div>
              <h3 className="font-display text-[20px] md:text-[22px] font-semibold text-foreground mb-3">The 5-Inch Rule and wide-plank reclaimed</h3>
              <p>
                Most reclaimed floors are wide plank  5 inches and up  because narrow boards from old-growth timber would be a waste of the material. NWFA installation guidelines require nail-plus-adhesive assist on any plank 5 inches or wider on wood subfloor. On reclaimed this is not optional. Wide plank moves more than narrow strip, and old-growth density means the plank will win any fight against a mechanical fastener alone. Every reclaimed installation we do on wood subfloor is nailed and glued per NWFA specification.
              </p>
            </div>

            <div>
              <h3 className="font-display text-[20px] md:text-[22px] font-semibold text-foreground mb-3">Subfloor flatness for reclaimed</h3>
              <p>
                The NWFA subfloor flatness standard  within a defined tolerance over a set span, per NWFA guidelines  is even more critical under wide-plank reclaimed. Reclaimed planks have less flex than engineered flooring, and any subfloor deviation telegraphs directly into the finished floor as hollow spots, gaps, or squeaks. Before a single plank goes down we survey the subfloor with a straightedge and self-level or plane whatever is out of tolerance. See our{" "}
                <Link to="/subfloor-leveling" className="text-primary underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors">subfloor leveling scope</Link> for how this is quoted and executed.
              </p>
            </div>

            <div>
              <h3 className="font-display text-[20px] md:text-[22px] font-semibold text-foreground mb-3">Choosing the right finish</h3>
              <p>
                Finish selection on reclaimed is not aesthetic-only  it's chemical. The right systems for reclaimed character surfaces are plant-based hardwax oils (Rubio Monocoat, Pallmann Magic Oil) or 2-component waterborne polyurethane. Both penetrate character marks cleanly, allow spot repair, and let the wood's natural depth read. Cheap oil-modified polyurethane fails on reclaimed for two reasons: it pools in nail holes and checks (drying to a plastic slick), and it ambers heavily over reclaimed patina, muddying the exact color the client bought the floor for. We do not install oil-modified poly on reclaimed. Ever.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ 3b · HVAC VENT HIGHLIGHT ══ */}
      <section className="bg-foreground py-14 md:py-20 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center">
            <motion.div {...fadeUp} className="relative">
              <div className="rounded-2xl overflow-hidden relative group">
                <img width={800} height={534} loading="lazy"
                  src={reclaimedVentImg}
                  alt="Flush brass honeycomb register set into a reclaimed antique oak floor, Lexington, MI"
                  className="w-full aspect-[4/3] object-cover transition-all duration-700 ease-out grayscale-[20%] contrast-[1.1] group-hover:grayscale-0 group-hover:scale-[1.04]"
                />
              </div>
            </motion.div>

            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }}>
              <p className="text-[10px] md:text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-4">
                Artisan Detail
              </p>
              <h2 className="font-display text-[clamp(24px,4vw,40px)] font-semibold text-primary-foreground leading-[1.12]">
                Preserving the Era: Authentic Architectural{" "}
                <em className="italic font-normal text-primary">Vents.</em>
              </h2>
              <p className="mt-5 text-sm md:text-[15px] font-light text-muted-foreground/80 leading-relaxed max-w-lg">
                When installing a century-old reclaimed floor, a modern drop-in vent instantly shatters the illusion. To preserve the authenticity, we custom-fabricate flush-mounted registers directly from the reclaimed material itself  matching the patina and grain to the surrounding planks. We can also integrate period-accurate raw steel or bronze flush-mounts. Our goal is to make the modern HVAC system completely disappear into the history of the floor.
              </p>
              <div className="flex flex-wrap gap-2 mt-6">
                {["Flush-Mount Registers", "Period-Accurate Hardware", "Grain-Matched", "Custom Fabricated"].map(
                  (chip) => (
                    <span
                      key={chip}
                      className="text-[9px] font-bold tracking-[.1em] uppercase text-[hsl(var(--gold-200))] border border-primary-foreground/10 rounded-md px-3 py-1.5 bg-primary-foreground/[.04]"
                    >
                      {chip}
                    </span>
                  )
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ 3d · MICHIGAN CLIMATE AND RECLAIMED (NEW) ══ */}
      <section className="bg-background py-14 md:py-20 lg:py-28 border-b border-border/40">
        <div className="max-w-[820px] mx-auto px-5 md:px-12">
          <motion.div {...fadeUp}>
            <p className="text-[11px] font-semibold tracking-[.1em] uppercase text-primary mb-2">Local Conditions</p>
            <h2 className="font-display text-[clamp(28px,5vw,48px)] font-semibold text-foreground leading-[1.1] mb-6">
              Michigan climate and reclaimed <em className="italic font-normal text-primary">wood flooring.</em>
            </h2>
            <div className="space-y-5 text-[14.5px] font-light text-muted-foreground leading-[1.85]">
              <p>
                Michigan swings hard between seasons  dry interior air under forced-air heat all winter, then humid summers that push interior relative humidity well above winter lows. Wide-plank reclaimed responds to that swing more than narrow-strip new hardwood does, which is why acclimation and HVAC coordination matter as much as the installation itself for reclaimed wood flooring Michigan projects.
              </p>
              <p>
                Equilibrium moisture content (EMC) targets are region-specific. Southeast Michigan  Metro Detroit, Ann Arbor, Livonia, Northville, Plymouth  runs a different indoor EMC range than Northern Michigan cottage country (Traverse City, Petoskey, Harbor Springs). We calibrate acclimation moisture targets to the actual home per NWFA guidelines rather than a generic national number, and we test each room individually before install day.
              </p>
              <p>
                Acclimation windows for reclaimed are longer than for new hardwood, and we hold the schedule until the wood and subfloor meet spec. Lakefront and seasonally-occupied homes need a different strategy again  a cottage that sits closed and dehumidified from October through May will drive a reclaimed floor toward gaps every winter unless HVAC is committed to during the off-season. We evaluate this during the site visit and set expectations before contract.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══ 3c · CURRENT PROJECT GALLERY ══ */}
      <ReclaimedProjectsGallery />

      {/* ══ 3e · HOW TO CHOOSE RECLAIMED (NEW) ══ */}
      <section className="bg-muted/30 py-14 md:py-20 lg:py-24 border-t border-border/40">
        <div className="max-w-[820px] mx-auto px-5 md:px-12">
          <motion.div {...fadeUp}>
            <p className="text-[11px] font-semibold tracking-[.1em] uppercase text-primary mb-2">Buyer's Guide</p>
            <h2 className="font-display text-[clamp(28px,5vw,48px)] font-semibold text-foreground leading-[1.1] mb-6">
              How to choose reclaimed flooring <em className="italic font-normal text-primary">for your home.</em>
            </h2>
            <div className="space-y-5 text-[14.5px] font-light text-muted-foreground leading-[1.85]">
              <p>
                <strong className="text-foreground font-semibold">Species by aesthetic.</strong> Reclaimed Oak and Hickory read traditional and hold up under high-traffic family use. Wormy Chestnut and Trophy Room blends read historic-estate. Douglas Fir and Beech/Maple lean lighter and more contemporary. Heart Pine sits in between  warm amber tones that work as easily in a modern kitchen as in a farmhouse.
              </p>
              <p>
                <strong className="text-foreground font-semibold">Plank width and character density.</strong> Wider planks mean fewer seams and a calmer overall floor even when each plank is character-forward. Narrower widths (still wide by modern standards  5 to 6 inches) read busier because there is more edge per square foot. Hit-Skip and Original Face faces read as high-character; Resawn and Appearance Grade faces read as calm reclaimed.
              </p>
              <p>
                <strong className="text-foreground font-semibold">Finish choice by lifestyle.</strong> Homes with kids and pets are usually best served by 2-component waterborne polyurethane  hardest wearing surface, easiest to spot-repair. Clients who want the natural low-sheen look and are willing to recoat every few years should be on hardwax oil (Rubio Monocoat or Pallmann Magic Oil).
              </p>
              <p>
                <strong className="text-foreground font-semibold">New construction vs renovation retrofit.</strong> New builds let us set HVAC commissioning and acclimation windows into the schedule from the start. Renovation retrofits work equally well but require a longer acclimation window in the existing home before install day. Either way, we handle moisture testing through final finish as one scope.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══ 3e2 · RECLAIMED PROJECTS ══ */}
      <section className="bg-background py-14 md:py-20">
        <div className="max-w-[820px] mx-auto px-5 md:px-12 text-center">
          <h2 className="font-display text-[clamp(24px,4vw,36px)] font-semibold text-foreground leading-[1.15] mb-8">
            Reclaimed projects in Michigan
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              { to: "/projects/lexington-reclaimed-oak", label: "Lexington reclaimed hit skip oak floor and Douglas fir ceiling" },
              { to: "/projects/howell-reclaimed-tobacco-pine", label: "Howell reclaimed Tobacco Pine floor" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="flex items-center justify-center rounded-xl border border-border bg-card px-6 py-5 text-sm md:text-[15px] font-medium text-foreground hover:border-primary hover:text-primary transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
          <p className="text-sm text-muted-foreground mt-4">More Michigan projects: <Link to="/projects/brighton-mercier-hickory" className="text-primary hover:text-primary/80">Mercier Hickory floor in Brighton, MI</Link></p>
        </div>
      </section>

      {/* ══ 3f · FAQ (NEW) ══ */}
      <PageFAQ
        subheading="Reclaimed Wood Flooring Michigan"
        heading="Reclaimed barnwood, straight answers."
        faqs={reclaimedFAQs}
      />

      {/* ══ 4 · BEYOND FLOORS: CEILINGS & BEAMS ══ */}
      <section className="bg-foreground py-14 md:py-20 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fadeUp} className="max-w-2xl mx-auto text-center">
            <p className="text-[10px] md:text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-4">
              Beyond Floors
            </p>
            <h2 className="font-display text-[clamp(26px,4.5vw,48px)] font-semibold text-primary-foreground leading-[1.1]">
              Architectural Wood Ceilings &amp;{" "}
              <em className="italic font-normal text-[hsl(var(--gold-300))]">Reclaimed Beams.</em>
            </h2>
            <p className="mt-5 text-sm md:text-[15px] font-light text-primary-foreground/55 leading-relaxed max-w-xl mx-auto">
              Our expertise goes well beyond what you walk on. We craft and install breathtaking reclaimed wood ceilings and architectural beams that transform the entire character of your space. From the ground up and the ceiling down.
            </p>
            <p className="mt-4 text-sm md:text-[15px] font-light text-primary-foreground/55 leading-relaxed max-w-xl mx-auto">
              Species selection for ceilings and beams is coordinated with the floor so patina, grain, and color read as one intentional palette rather than three separate purchases. Structural beam installations carry a different technical bar than floors  load path, connection detailing, and moisture behavior of large-section reclaimed timber all matter. As NWFA Certified Wood Flooring Inspectors and a Licensed Michigan Residential Builder, we handle beam scopes most flooring contractors will not touch.
            </p>
            <p className="mt-4 text-sm md:text-[15px] font-light text-primary-foreground/55 leading-relaxed max-w-xl mx-auto">
              See a finished example in our{" "}
              <Link to="/projects/lexington-reclaimed-oak" className="text-[hsl(var(--gold-300))] underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors">Lexington reclaimed oak floor and Douglas fir ceiling</Link>{" "}
              project.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
              <QuoteModal
                trigger={
                  <button className="bg-primary text-primary-foreground px-8 py-3.5 rounded-full text-sm font-medium hover:bg-[hsl(var(--gold-400))] transition-colors">
                    Discuss Your Project →
                  </button>
                }
              />
              <Link
                to="/brands/olde-wood/antique-reclaimed"
                className="border border-primary-foreground/25 text-primary-foreground px-7 py-3.5 rounded-full text-sm font-medium hover:border-primary-foreground/60 transition-colors"
              >
                Explore the Olde Wood Limited Antique Catalog →
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══ 5 · BOTTOM DUAL CTA ══ */}
      <section className="bg-primary py-10 md:py-14">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-[clamp(24px,3vw,36px)] font-semibold text-primary-foreground leading-[1.1] mb-2">
              See reclaimed species in person.
            </h3>
            <p className="text-[13px] font-light text-primary-foreground/65 leading-relaxed">
              Visit our Livonia showroom to handle Olde Wood Antique Reclaimed samples across all 20 species, or schedule a consultation and we'll walk your project in person.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0 w-full md:w-auto">
            <QuoteModal
              trigger={
                <button className="bg-primary-foreground text-primary px-7 py-3.5 rounded-xl text-xs font-bold tracking-[.05em] uppercase hover:-translate-y-px transition-transform whitespace-nowrap text-center">
                  Schedule a Consultation →
                </button>
              }
            />
            <a
              href="tel:2483258853"
              className="border border-primary-foreground/30 text-primary-foreground px-7 py-3.5 rounded-xl text-xs font-semibold tracking-[.05em] hover:border-primary-foreground hover:text-primary-foreground transition-colors whitespace-nowrap text-center"
            >
              📞 (248) 325-8853
            </a>
          </div>
        </div>
      </section>

      </main>
      <SiteFooter />
    </div>
  );
};

export default ReclaimedBarnwood;
